import styled from "@emotion/styled";
import { Bar } from "react-chartjs-2";
import { ThemeProps } from "types/theme";
import { withTheme } from "@emotion/react";
import {
  Align,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  LinearScale,
  Title,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { GenderAgePercentages } from "core/types/social.types";
import { Grid, Typography } from "@mui/material";
import { Text } from "components/atoms/text";
import PersonIcon from "@mui/icons-material/Person";
import { grey } from "@mui/material/colors";

ChartJS.register(LinearScale, CategoryScale, BarElement, Title);

const ChartWrapper = styled.div`
  position: relative;
`;

type Props = {
  genderAgePercentages: GenderAgePercentages;
};

const AgeChart = ({ theme, genderAgePercentages }: ThemeProps & Props) => {
  const { t } = useTranslation();
  const ageGroups = genderAgePercentages.values.F
    ? Object.keys(genderAgePercentages.values.F)
    : [];
  const femalePercentages = genderAgePercentages.values.F
    ? Object.values(genderAgePercentages.values.F)
    : [];
  const malePercentages = genderAgePercentages.values.M
    ? Object.values(genderAgePercentages.values.M)
    : [];
  const malePercentage = genderAgePercentages.values.M
    ? Object.values(genderAgePercentages.values.M).reduce(
        (sum, value) => sum + value,
        0
      )
    : 0;
  const femalePercentage = genderAgePercentages.values.F
    ? Object.values(genderAgePercentages.values.F).reduce(
        (sum, value) => sum + value,
        0
      )
    : 0;

  const adultPercentageFemales = genderAgePercentages.values.F
    ? Object.entries(genderAgePercentages.values.F)
        .filter(([age]) => age !== "13-17")
        .reduce((sum, [, value]) => sum + value, 0)
    : 0;

  const adultPercentageMales = genderAgePercentages.values.M
    ? Object.entries(genderAgePercentages.values.M)
        .filter(([age]) => age !== "13-17")
        .reduce((sum, [, value]) => sum + value, 0)
    : 0;
  const totalPercentageAdults = adultPercentageFemales + adultPercentageMales;

  const data = {
    labels: ageGroups,
    datasets: [
      {
        label: "Uomini",
        data: malePercentages,
        backgroundColor: "rgba(54, 162, 235, 0.8)",
        borderRadius: 6,
        barThickness: 12,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
      {
        label: "Donne",
        data: femalePercentages,
        backgroundColor: "rgba(255, 99, 132, 0.8)",
        borderRadius: 6,
        barThickness: 12,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: t("social-reports.audience.age_title"),
        align: "start" as Align,
        font: {
          weight: "bold",
        },
        padding: 15,
        fullSize: true,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            label += context.parsed.y + "%";
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        type: "linear",
        // display: false,
        stacked: false,
        ticks: {
          stepSize: 20,
          callback: function (value) {
            return value + "%";
          },
        },
      },
      x: {
        stacked: false,
        grid: {
          color: "transparent",
        },
      },
    },
  };

  return (
    <>
      <ChartWrapper>
        <Bar data={data} options={options} />
      </ChartWrapper>
      {malePercentage === 0 &&
        femalePercentage === 0 &&
        totalPercentageAdults === 0 && (
          <Typography>{t("empty_states.empty_stats.title")}</Typography>
        )}
      <Grid display={"flex"} alignItems={"center"}>
        {malePercentage ? (
          <Grid
            item
            margin={"5px"}
            padding={"3px 10px"}
            borderRadius={"20px"}
            border={`0.5px solid ${theme.palette.divider}`}
            display={"flex"}
            alignItems={"center"}
          >
            <div
              style={{
                borderRadius: "15px",
                background: "rgba(54, 162, 235, 0.8)",
                width: "10px",
                height: "10px",
              }}
            ></div>
            <Text ml={"5px"}>
              {t("social-reports.audience.M") +
                ", " +
                malePercentage.toFixed(2) +
                "%"}
            </Text>
          </Grid>
        ) : (
          <></>
        )}
        {femalePercentage ? (
          <Grid
            item
            margin={"5px"}
            padding={"3px 10px"}
            borderRadius={"20px"}
            border={`0.5px solid ${theme.palette.divider}`}
            display={"flex"}
            alignItems={"center"}
          >
            <div
              style={{
                borderRadius: "15px",
                background: "rgba(255, 99, 132, 0.8)",
                width: "10px",
                height: "10px",
              }}
            ></div>
            <Text ml={"5px"}>
              {t("social-reports.audience.F") +
                ", " +
                femalePercentage.toFixed(2) +
                "%"}
            </Text>
          </Grid>
        ) : (
          <></>
        )}
        {totalPercentageAdults ? (
          <Grid
            item
            margin={"5px"}
            padding={"3px 10px"}
            display={"flex"}
            alignItems={"center"}
          >
            <PersonIcon
              fontSize="small"
              style={{
                color: grey[500],
              }}
            />
            <Text
              ml={"5px"}
              sx={{
                color: grey[500],
              }}
            >
              {t("social-reports.audience.adults") +
                " - " +
                totalPercentageAdults.toFixed(2) +
                "%"}
            </Text>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </>
  );
};

export default withTheme(AgeChart);
